import {
  Component,
  Element,
  getAssetPath,
  h,
  Host,
  Prop,
  State
} from "@stencil/core";

import {
  ChRadioGroupRenderCustomEvent,
  RadioGroupModel
} from "@genexus/chameleon-controls-library";
import { MercuryBundles } from "@genexus/mercury";
import { Locale } from "../../../common/locale";
import { FormSubmitResult } from "../../../components";
import {
  clearControlError,
  ControlValidation,
  validateControls
} from "../../../common/form-validation";
import { config } from "../../../common/config";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form",
  "utils/spacing",
  "utils/layout",
  "components/checkbox",
  "components/button",
  "components/edit",
  "components/radio-group",
  "components/tooltip",
  "utils/layout"
];

const MERGE_ACTION_OPTIONS = {
  fromCommitDate: {
    value: "fromCommitDate"
  },
  fromCommitNumber: {
    value: "fromCommitNumber"
  }
};

@Component({
  tag: "gx-ide-team-dev-bring-all-changes-selector",
  styleUrl: "bring-all-changes-selector.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/team-dev-bring-all-changes-selector"]
})
export class GxIdeBringAllChangesSelector {
  #componentLocale: any;
  #revisionNumberRef: HTMLChEditElement;
  #revisionDateRef: HTMLChEditElement;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/team-dev-bring-all-changes-selector/shortcuts.json`
  );

  @Element() el: HTMLGxIdeTeamDevBringAllChangesSelectorElement;
  /**
   * Date from which the commits will be merged
   */
  @State() sinceCommitDate: Date | undefined = undefined;

  /**
   * Number from which the commits will be merged
   */
  @State() sinceCommitNumber: number | undefined = undefined;

  /**
   * The selected option from the radio group.
   */
  @State() selectedOption: MergeActionTaken = MERGE_ACTION_OPTIONS
    .fromCommitDate.value as MergeActionTaken;

  /**
   * Model for the radio group.
   */
  @State() radioGroupModel: RadioGroupModel = [];

  /**
   * Map of validatable controls in the form.
   */
  @State() validatableControls = new Map<string, ControlValidation>();

  /**
   * Callback that must be invoked when the 'Cancel' button is pressed.
   */
  @Prop() readonly cancelCallback!: () => Promise<void>;

  /**
   * Callback that must be invoked when the 'Confirm' button is pressed.
   */
  @Prop() readonly confirmCallback!: (
    selectedMergeActionData: SelectedMergeActionData
  ) => Promise<FormSubmitResult>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.radioGroupModel = [
      {
        value: MERGE_ACTION_OPTIONS.fromCommitDate.value,
        caption: this.#componentLocale.captions.commitDate
      },
      {
        value: MERGE_ACTION_OPTIONS.fromCommitNumber.value,
        caption: this.#componentLocale.captions.commitNumber
      }
    ];
  }

  componentDidRender() {
    if (this.selectedOption === "fromCommitDate" && this.#revisionDateRef) {
      this.#initializeValidatableControls([this.#revisionDateRef]);
    }

    if (this.selectedOption === "fromCommitNumber" && this.#revisionNumberRef) {
      this.#initializeValidatableControls([this.#revisionNumberRef]);
    }
  }

  // TODO: evaluate if this should be a global helper function.
  // It is used on other dialogs as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  #initializeValidatableControls = (validatableControls: HTMLElement[]) => {
    validatableControls.forEach(validatableControl => {
      if (
        validatableControl.id &&
        !this.validatableControls.has(validatableControl.id)
      ) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #cancelClickHandler = async () => {
    await this.cancelCallback();
  };

  #confirmClickHandler = async () => {
    let formSubmitResult: FormSubmitResult;
    const selectedMergeActionData: SelectedMergeActionData = {
      operationType: this.selectedOption,
      fromDate:
        this.selectedOption === "fromCommitDate"
          ? this.sinceCommitDate
          : undefined,
      fromRevision:
        this.selectedOption === "fromCommitNumber"
          ? this.sinceCommitNumber
          : undefined
    };
    formSubmitResult = await this.confirmCallback(selectedMergeActionData);
    this.validatableControls = validateControls(
      formSubmitResult,
      this.validatableControls
    );
  };

  #handleLastDates = (lastDates: LastDates) => {
    const currentDate = new Date();
    let sinceDate = new Date();
    switch (lastDates) {
      case "lastSevenDays":
        sinceDate.setDate(currentDate.getDate() - 7);
        break;
      case "lastThirtyDays":
        sinceDate.setDate(currentDate.getDate() - 30);
        break;
      case "lastNinetyDays":
        sinceDate.setDate(currentDate.getDate() - 90);
        break;
    }
    this.sinceCommitDate = sinceDate;
  };

  #handleCommitDateValueChanged = (event: CustomEvent<string> | InputEvent) => {
    const value = event.detail as string;
    if (value && value !== "") {
      const [year, month, day] = value.split("-").map(Number);
      this.sinceCommitDate = new Date(year, month - 1, day);
    } else {
      this.sinceCommitDate = undefined;
    }
    clearControlError(this.validatableControls, "commit-date");
  };

  #handleCommitNumberValueChanged = (
    event: CustomEvent<string> | InputEvent
  ) => {
    this.sinceCommitNumber = Number(event.detail);
    clearControlError(this.validatableControls, "commit-number");
  };

  #handleRadioGroupValueChange = (
    event: ChRadioGroupRenderCustomEvent<string>
  ) => {
    this.selectedOption = event.detail as MergeActionTaken;
    if (this.selectedOption === "fromCommitDate") {
      this.sinceCommitNumber = undefined;
      clearControlError(this.validatableControls, "commit-number");
    } else if (this.selectedOption === "fromCommitNumber") {
      this.sinceCommitDate = undefined;
      clearControlError(this.validatableControls, "commit-date");
    }
  };

  #confirmButtonDisabled = (): boolean => {
    if (this.selectedOption === "fromCommitDate") {
      // Validate that sinceCommitDate is a valid date
      return !(
        this.sinceCommitDate instanceof Date &&
        !isNaN(this.sinceCommitDate.getTime())
      );
    } else if (this.selectedOption === "fromCommitNumber") {
      // Validate that sinceCommitNumber is a valid number
      return !(this.sinceCommitNumber > 0);
    }
    return true;
  };

  #renderCommitDateInput = () => {
    if (this.selectedOption !== "fromCommitDate") {
      return null;
    }
    return (
      <div class="layout layout--cols-2-1">
        <div class="layout__panel">
          <div class="field field-block">
            <label class="label" htmlFor="commit-date">
              {this.#componentLocale.labels.commitDate}
            </label>
            <ch-edit
              class={{
                "input": true,
                "input-error":
                  this.validatableControls.get("commit-date")?.hasError
              }}
              id="commit-date"
              type="date"
              value={
                this.sinceCommitDate &&
                this.sinceCommitDate.toISOString().split("T")[0]
              }
              onInput={this.#handleCommitDateValueChanged}
              ref={(el: HTMLChEditElement) =>
                (this.#revisionDateRef = el as HTMLChEditElement)
              }
            />
            {this.#evaluateTooltipRender(this.#revisionDateRef)}
          </div>
        </div>
      </div>
    );
  };

  #renderCommitNumberInput = () => {
    if (this.selectedOption !== "fromCommitNumber") {
      return null;
    }
    return (
      <div class="layout layout--cols-2-1">
        <div class="layout__panel">
          <div class="field field-block">
            <label class="label" htmlFor="commit-number">
              {this.#componentLocale.labels.commitNumber}
            </label>
            <ch-edit
              class={{
                "input": true,
                "input-error":
                  this.validatableControls.get("commit-number")?.hasError
              }}
              id="commit-number"
              type="number"
              placeholder={this.#componentLocale.placeholders.commitNumber}
              onInput={this.#handleCommitNumberValueChanged}
              mode="numeric"
              ref={(el: HTMLChEditElement) =>
                (this.#revisionNumberRef = el as HTMLChEditElement)
              }
            />
            {this.#evaluateTooltipRender(this.#revisionNumberRef)}
          </div>
        </div>
      </div>
    );
  };

  #renderLastDatesButtons = () => {
    if (this.selectedOption !== "fromCommitDate") {
      return null;
    }

    return (
      <div class="layout layout--cols-3">
        <div class="layout__panel">
          <button
            id="last-seven-days-button"
            class="button-secondary"
            onClick={() => this.#handleLastDates("lastSevenDays")}
          >
            {this.#componentLocale.captions.lastSevenDays}
          </button>
        </div>
        <div class="layout__panel">
          <button
            id="last-thirty-days-button"
            class="button-secondary"
            onClick={() => this.#handleLastDates("lastThirtyDays")}
          >
            {this.#componentLocale.captions.lastThirtyDays}
          </button>
        </div>
        <div class="layout__panel">
          <button
            id="last-ninety-days-button"
            class="button-secondary"
            onClick={() => this.#handleLastDates("lastNinetyDays")}
          >
            {this.#componentLocale.captions.lastNinetyDays}
          </button>
        </div>
      </div>
    );
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts src={this.#shortcutsSrc}></ch-shortcuts>
        <div
          class={{
            "spacing-body": true,
            "commit-date-container": this.selectedOption === "fromCommitDate",
            "commit-number-container":
              this.selectedOption === "fromCommitNumber"
          }}
        >
          <p class="body-regular-m heading">
            {this.#componentLocale.labels.selectRange}
          </p>
          <ch-radio-group-render
            class="radio-group"
            direction="horizontal"
            model={this.radioGroupModel}
            onChange={this.#handleRadioGroupValueChange}
            value={MERGE_ACTION_OPTIONS.fromCommitDate.value}
          />

          {this.#renderLastDatesButtons()}
          {this.#renderCommitDateInput()}
          {this.#renderCommitNumberInput()}
        </div>

        <footer class="dialog-footer-with-border spacing-body">
          <div class="buttons-spacer">
            <button
              class="button-secondary"
              id="cancel-button"
              onClick={this.#cancelClickHandler}
            >
              {this.#componentLocale.buttons.cancel}
            </button>

            <button
              id="confirm-button"
              class="button-primary"
              onClick={this.#confirmClickHandler}
              disabled={this.#confirmButtonDisabled()}
            >
              {this.#componentLocale.buttons.confirm}
            </button>
          </div>
        </footer>
      </Host>
    );
  }
}

export type SelectedMergeActionData = {
  operationType: MergeActionTaken;
  fromDate?: Date;
  fromRevision?: number;
};

export type MergeActionTaken = "fromCommitDate" | "fromCommitNumber";

export type LastDates = "lastSevenDays" | "lastThirtyDays" | "lastNinetyDays";
